import { create } from 'zustand';
import { seriesService, CreateSeriesDto, UpdateSeriesDto, GetPaginationSeriesDto, Series } from '@/services/seriesService';

interface SeriesState {
    seriesList: Series[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;

    fetchSeries: (query?: GetPaginationSeriesDto) => Promise<void>;
    fetchSeriesById: (id: string) => Promise<Series | null>;
    createSeries: (data: CreateSeriesDto) => Promise<boolean>;
    updateSeries: (id: string, data: UpdateSeriesDto) => Promise<boolean>;
    deleteSeries: (id: string) => Promise<boolean>;
}

export const useSeriesStore = create<SeriesState>()((set, get) => ({
    seriesList: [],
    pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,

    fetchSeries: async (query) => {
        set({ isLoading: true, error: null });
        try {
            const res = await seriesService.findAll(query || { page: get().pagination.page, limit: get().pagination.limit });
            if (res.ok && res.data) {
                const data: any = res.data.data;
                set({
                    seriesList: data?.data || data?.series || data?.items || [],
                    pagination: data?.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 },
                    isLoading: false,
                });
            } else {
                set({ isLoading: false, error: (res as any)?.data?.message || 'Lỗi khi tải series' });
            }
        } catch (e) {
            set({ isLoading: false, error: e instanceof Error ? e.message : 'Lỗi khi tải series' });
        }
    },

    fetchSeriesById: async (id) => {
        try {
            const res = await seriesService.findOne(id);
            if (res.ok && res.data) {
                return (res.data.data?.data as Series) || (res.data.data as Series);
            }
            return null;
        } catch {
            return null;
        }
    },

    createSeries: async (data) => {
        set({ isCreating: true, error: null });
        try {
            const res = await seriesService.create(data);
            set({ isCreating: false });
            if (res.ok) {
                await get().fetchSeries();
                return true;
            }
            return false;
        } catch (e) {
            set({ isCreating: false, error: e instanceof Error ? e.message : 'Lỗi khi tạo series' });
            return false;
        }
    },

    updateSeries: async (id, data) => {
        set({ isUpdating: true, error: null });
        try {
            const res = await seriesService.update(id, data);
            set({ isUpdating: false });
            if (res.ok) {
                await get().fetchSeries();
                return true;
            }
            return false;
        } catch (e) {
            set({ isUpdating: false, error: e instanceof Error ? e.message : 'Lỗi khi cập nhật series' });
            return false;
        }
    },

    deleteSeries: async (id) => {
        set({ isDeleting: true, error: null });
        try {
            const res = await seriesService.remove(id);
            set({ isDeleting: false });
            if (res.ok) {
                await get().fetchSeries();
                return true;
            }
            return false;
        } catch (e) {
            set({ isDeleting: false, error: e instanceof Error ? e.message : 'Lỗi khi xóa series' });
            return false;
        }
    },
}));


